/**
 * Short ids and Telegram deep-link payloads.
 *
 * `spec.md` §11.9 step 11: `short_id` is **8 base64url characters derived from
 * `plate_seed` plus the row id**, uniqueness-checked against `tracks.short_id` with a
 * counter salt on collision. `spec.md` §12.3: the `startapp` payload alphabet is
 * *exactly* `[A-Za-z0-9_-]` (TDLib's `is_valid_start_parameter` is
 * `is_base64url_characters`), and TDLib enforces it **asymmetrically** — the charset is
 * checked on `t.me/<bot>?startapp=X` and `tg://resolve?…&startapp=`, but not on
 * `t.me/<bot>/<app>?startapp=X`. An out-of-alphabet id therefore appears to work on one
 * link shape and silently fails on the other, which is the worst possible failure mode.
 * So: every id this module emits is inside the alphabet by construction, and everything
 * it parses is validated against it.
 *
 * Determinism has the same contract as `plate.ts`: no randomness, no clock, no state.
 * The salt is an explicit caller-supplied counter, not an entropy source — the indexer
 * increments it only when the database reports a real uniqueness collision, so a
 * reindex of the same rows reproduces the same ids.
 *
 * This module imports nothing.
 */
/* ───────────────────────────── the alphabet ────────────────────────────────── */
/**
 * base64url, in the RFC 4648 §5 order. Index-stable: reordering it re-mints every
 * short id, so this is part of the data contract.
 */
const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
/** Every short id is exactly this many characters. */
export const SHORT_ID_LENGTH = 8;
/**
 * `startapp` payloads must stay at or under 64 characters.
 *
 * `spec.md` §12.3 flags this as an `ASSUMPTION:` — no primary source states a maximum
 * for `startapp`; the documented 64-character ceiling is for `?start=` and for
 * `InlineQueryResultsButton.start_parameter`. Ours are 9 characters, so the ceiling is
 * never in play; it is enforced anyway so a future longer payload fails a test here
 * rather than on a device.
 */
export const MAX_START_PARAM_LENGTH = 64;
/** `^[A-Za-z0-9_-]{8}$` — a well-formed short id. */
const SHORT_ID_RE = /^[A-Za-z0-9_-]{8}$/;
/** `^[A-Za-z0-9_-]{1,64}$` — TDLib's `is_valid_start_parameter`, plus the length cap. */
const START_PARAM_RE = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * TDLib routes any payload beginning `_tgr_` to referral handling, so it can never be
 * one of ours (`spec.md` §12.3).
 */
const RESERVED_PREFIX = '_tgr_';
/* ─────────────────────────── the id derivation ─────────────────────────────── */
/** Coerce to uint32 the way the bitwise operators do, including for values ≥ 2^32. */
function u32(n) {
    return n >>> 0;
}
/**
 * Murmur3's `fmix32` finaliser: an avalanche step that spreads every input bit across
 * all 32 output bits.
 *
 * FNV-1a (used for the plate seed) has weak low bits — sequential inputs produce
 * sequential-ish tails. Short ids are cut from 6-bit slices, so weak bits would make
 * neighbouring rows share visible prefixes and cluster their collisions. `fmix32` is
 * pure integer maths (`Math.imul`, `>>>`) and so is bit-identical in every engine.
 */
function fmix32(x) {
    let h = u32(x);
    h ^= h >>> 16;
    h = Math.imul(h, 0x85ebca6b) >>> 0;
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35) >>> 0;
    h ^= h >>> 16;
    return h >>> 0;
}
/**
 * Derive the 8-character base64url short id for a row.
 *
 * 8 × 6 = **48 bits** of id space, taken as the low 24 bits of two independently mixed
 * words. At the channel's scale (tens of thousands of tracks) the birthday collision
 * probability is ~1 in 10^6 for 10^4 rows, and a real collision is handled by the
 * caller retrying with `salt + 1` — which is why the salt is threaded through *both*
 * words rather than appended, so a salt bump changes every character rather than the
 * tail.
 *
 * `rowId` is a SQLite `INTEGER PRIMARY KEY` and so can exceed 2^32; both halves are
 * folded in, which is why the high word is computed by division rather than by `>>>`.
 *
 * @param seed  the track's `plate_seed` (any uint32; larger values are wrapped).
 * @param rowId the row's primary key. Must be a non-negative safe integer.
 * @param salt  collision counter, default `0`. Must be a non-negative safe integer.
 * @throws TypeError if any argument is not a finite, non-negative safe integer.
 */
export function shortIdFrom(seed, rowId, salt = 0) {
    assertIdInput('seed', seed);
    assertIdInput('rowId', rowId);
    assertIdInput('salt', salt);
    const lo = u32(rowId);
    const hi = u32(Math.floor(rowId / 4294967296));
    const s = u32(salt);
    // Fold the row id's two halves into one word first, then feed *all three* inputs into
    // *both* output words. Every input must reach every character: an earlier draft fed
    // only the high half of the row id into `w0`, and because that half is 0 for every
    // realistic row, two tracks sharing a seed (exactly what a re-post produces) came out
    // sharing their first four characters. 0x9e3779b9 is the golden-ratio constant; the
    // other multipliers are the standard Murmur/xxHash mixing primes — all odd, so none
    // of them can lose a bit.
    const row = u32(lo ^ Math.imul(hi + 1, 0x9e3779b9));
    const w0 = fmix32(u32(seed) ^ Math.imul(row + 1, 0x27d4eb2f) ^ Math.imul(s + 1, 0x85ebca6b));
    const w1 = fmix32(row ^ Math.imul(w0 ^ (s + 1), 0x165667b1) ^ Math.imul(u32(seed) + 1, 0xc2b2ae35));
    let out = '';
    for (let i = 0; i < 4; i++)
        out += BASE64URL.charAt((w0 >>> (i * 6)) & 63);
    for (let i = 0; i < 4; i++)
        out += BASE64URL.charAt((w1 >>> (i * 6)) & 63);
    return out;
}
function assertIdInput(name, value) {
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new TypeError(`shortIdFrom: ${name} must be a non-negative safe integer, received ${String(value)}`);
    }
}
/**
 * Whether `s` is a syntactically valid short id (exactly 8 base64url characters).
 *
 * The `typeof` guard is not redundant despite the parameter type: these predicates are
 * the boundary between validated and unvalidated data and are called with values that
 * came off the wire, and `RegExp.test(undefined)` coerces to the string `"undefined"` —
 * which is inside the base64url alphabet and would pass. Hence the `unknown` local.
 */
export function isValidShortId(s) {
    const v = s;
    return typeof v === 'string' && SHORT_ID_RE.test(v);
}
/**
 * Whether `s` may be used as a Telegram `startapp` / `start` payload.
 *
 * Rejects the empty string, anything over {@link MAX_START_PARAM_LENGTH}, every
 * character outside `[A-Za-z0-9_-]` (so `.`, `:`, `=`, `%`, `/`, whitespace and all
 * non-ASCII), and the reserved `_tgr_` prefix. The regex is anchored and has no
 * alternation, so it cannot backtrack.
 */
export function isValidStartParam(s) {
    const v = s;
    return typeof v === 'string' && START_PARAM_RE.test(v) && !v.startsWith(RESERVED_PREFIX);
}
/* ───────────────────────────── deep-link payloads ──────────────────────────── */
/**
 * The one-character type tags (`spec.md` §12.3): `t` track, `p` playlist, `a` artist.
 * Frozen and order-stable for exhaustiveness tests.
 */
export const DEEP_LINK_KINDS = Object.freeze(['t', 'p', 'a']);
/** The payload that opens the app at its root. Not a short id, and has no tag. */
export const DEEP_LINK_HOME = 'home';
function isDeepLinkKind(c) {
    return c === 't' || c === 'p' || c === 'a';
}
/**
 * Build the `startapp` payload for a target: the one-character tag followed by the
 * short id, e.g. `t9fK2mQ1`.
 *
 * Returns the **payload only**, never a URL — the bot username and the Mini App short
 * name are runtime configuration, and a helper that baked them in here would be a
 * second source of truth for them.
 *
 * @throws TypeError if `shortId` is not 8 base64url characters. Emitting an invalid
 * payload is worse than throwing: it would work on the named link shape and silently
 * fail on the Main-Mini-App shape (`spec.md` §12.3).
 */
export function encodeDeepLink(kind, shortId) {
    if (!isValidShortId(shortId)) {
        throw new TypeError(`encodeDeepLink: shortId must be 8 base64url characters, received ${JSON.stringify(shortId)}`);
    }
    if (!isDeepLinkKind(kind)) {
        throw new TypeError(`encodeDeepLink: unknown kind ${JSON.stringify(kind)}`);
    }
    return `${kind}${shortId}`;
}
/**
 * Parse a `start_param` payload.
 *
 * **Read the value from the signed `initDataUnsafe.start_param`**, never from the
 * unsigned `tgWebAppStartParam` query parameter (`spec.md` §12.3) — the signed copy
 * lives inside the hashed query string and so is genuinely tamper-proof.
 *
 * Returns `null` — never throws — for anything unrecognised, including `undefined`, the
 * empty string, an unknown tag, a malformed short id and any out-of-alphabet payload.
 * A bad deep link must land the user on the home screen, not on an error.
 */
export function parseDeepLink(startParam) {
    if (typeof startParam !== 'string' || !isValidStartParam(startParam))
        return null;
    if (startParam === DEEP_LINK_HOME)
        return { kind: 'home' };
    const tag = startParam.charAt(0);
    if (!isDeepLinkKind(tag))
        return null;
    const shortId = startParam.slice(1);
    if (!isValidShortId(shortId))
        return null;
    return { kind: tag, shortId };
}
//# sourceMappingURL=ids.js.map